import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UtensilsCrossed,
  Clock,
  Check,
  Flame,
  ChevronDown,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { useTimeFormatter } from "@/lib/timeFormat";

// Map meal name to emoji thumbnail
function mealEmoji(name = "") {
  const n = name.toLowerCase();
  if (/breakfast|egg|oat|cereal|pancake/.test(n)) return "🍳";
  if (/mid.*morning|snack|nut|almond/.test(n)) return "🥜";
  if (/lunch|rice|bowl|beef|chicken/.test(n)) return "🍚";
  if (/pre.?workout|shake|whey|protein/.test(n)) return "🥤";
  if (/post.?workout/.test(n)) return "🥗";
  if (/dinner|salmon|fish|steak/.test(n)) return "🍱";
  if (/fruit|banana|apple/.test(n)) return "🍌";
  return "🍽️";
}

export default function DailyMealPreviewDialog({
  open,
  onOpenChange,
  activePlan,
  todaysMeals = [],
  manualLogs = [],
  completedMeals = new Set(),
  onToggleMeal,
  onToggleManual,
  isMutating = false,
  highlightMealName = null,
}) {
  const fmtTime = useTimeFormatter();
  const [expandedMealIdx, setExpandedMealIdx] = useState(0);

  // If a specific meal was clicked in Your Next Meal, expand it
  useMemo(() => {
    if (highlightMealName && todaysMeals.length > 0) {
      const idx = todaysMeals.findIndex(
        (m) =>
          m.meal_name?.toLowerCase() === highlightMealName.toLowerCase()
      );
      if (idx >= 0) setExpandedMealIdx(idx);
    }
  }, [highlightMealName, todaysMeals]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg w-[calc(100%-2rem)] max-h-[88vh] flex flex-col p-0 overflow-hidden rounded-2xl border border-white/10 text-white shadow-2xl"
        style={{
          background: "radial-gradient(ellipse at top, #101B2E 0%, #080F1D 100%)",
        }}
      >
        <DialogHeader className="p-4 sm:p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black tracking-widest text-sky-400 uppercase">
                {activePlan?.name || "TODAY'S NUTRITION"}
              </p>
              <DialogTitle className="text-xl sm:text-2xl font-display font-black text-white uppercase tracking-tight">
                Daily Meal Preview
              </DialogTitle>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-sky-500/15 border border-sky-400/30 text-sky-300">
              {todaysMeals.length + manualLogs.length} Meals
            </span>
          </div>
        </DialogHeader>
        <HealthSourceNote topic="food" />

        {/* SCROLLABLE MEAL LIST */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3.5 divide-y-0">
          {todaysMeals.length === 0 && manualLogs.length === 0 ? (
            <div className="text-center py-10 space-y-2">
              <UtensilsCrossed className="w-10 h-10 mx-auto text-slate-500" />
              <p className="text-sm font-semibold text-slate-300">
                No planned meals scheduled for today.
              </p>
              <p className="text-xs text-slate-500">
                You can assign a meal plan in the Meals tab or log food on the fly.
              </p>
            </div>
          ) : (
            todaysMeals.map((meal, idx) => {
              const mealKey = `${meal.plan_id}-${meal.meal_name}`;
              const isLogged = completedMeals.has(mealKey);
              const isOpen = expandedMealIdx === idx;

              // Calculate meal macros & calories
              const foods = meal.foods || [];
              const mealCalories = foods.reduce(
                (sum, f) => sum + (Number(f.calories) || 0),
                0
              );
              const mealProtein = Math.round(
                foods.reduce((sum, f) => sum + (Number(f.protein_g) || 0), 0)
              );
              const mealCarbs = Math.round(
                foods.reduce((sum, f) => sum + (Number(f.carbs_g) || 0), 0)
              );
              const mealFats = Math.round(
                foods.reduce((sum, f) => sum + (Number(f.fats_g) || 0), 0)
              );

              return (
                <div
                  key={idx}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    isLogged
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : isOpen
                      ? "bg-white/[0.06] border-sky-400/40 shadow-[0_0_15px_rgba(56,189,248,0.15)]"
                      : "bg-white/[0.03] border-white/10 hover:bg-white/[0.05]"
                  }`}
                >
                  {/* MEAL HEADER BAR */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedMealIdx(isOpen ? -1 : idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedMealIdx(isOpen ? -1 : idx);
                      }
                    }}
                    className="p-3.5 flex items-center justify-between gap-3 cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-xl shrink-0">
                        {mealEmoji(meal.meal_name)}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-display font-bold text-sm sm:text-base text-white truncate">
                            {meal.meal_name}
                          </h4>
                          {isLogged && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                              LOGGED ✓
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span className="text-sky-400 font-bold">
                            {mealCalories} kcal
                          </span>
                          {meal.time && (
                            <>
                              <span>·</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-500" />
                                {fmtTime(meal.time)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isMutating) onToggleMeal(meal.plan_id, meal.meal_name);
                        }}
                        disabled={isMutating}
                        aria-label={isLogged ? "Unmark meal" : "Mark meal logged"}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          isLogged
                            ? "bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                            : "border-2 border-white/30 hover:border-sky-400 text-transparent"
                        }`}
                      >
                        <Check className="w-4 h-4 stroke-[3]" />
                      </button>
                      <ChevronDown
                        className={`w-4 h-4 text-slate-400 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>

                  {/* EXPANDED DETAILS: INGREDIENTS, MACROS, PREPARATION INSTRUCTIONS */}
                  {isOpen && (
                    <div className="px-3.5 pb-4 pt-1 space-y-3.5 border-t border-white/10 text-xs">
                      {/* Macro Breakdown Pills */}
                      <div className="grid grid-cols-3 gap-2 text-center pt-2">
                        <div className="p-2 rounded-xl bg-white/5 border border-white/5">
                          <p className="font-extrabold text-emerald-400 text-sm">
                            {mealProtein}g
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">
                            Protein
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-white/5 border border-white/5">
                          <p className="font-extrabold text-sky-400 text-sm">
                            {mealCarbs}g
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">
                            Carbs
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-white/5 border border-white/5">
                          <p className="font-extrabold text-amber-400 text-sm">
                            {mealFats}g
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">
                            Fat
                          </p>
                        </div>
                      </div>

                      {/* Ingredients & Portions List */}
                      {foods.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                            Ingredients & Portions
                          </p>
                          <div className="rounded-xl bg-black/20 border border-white/5 divide-y divide-white/5 overflow-hidden">
                            {foods.map((food, fIdx) => (
                              <div
                                key={fIdx}
                                className="p-2.5 flex items-center justify-between gap-2"
                              >
                                <div className="min-w-0">
                                  <p className="font-bold text-white truncate">
                                    {food.food_name}
                                  </p>
                                  {food.portion && (
                                    <p className="text-[11px] text-slate-400">
                                      {food.portion}
                                    </p>
                                  )}
                                </div>
                                <span className="text-[11px] font-mono font-semibold text-sky-300 shrink-0">
                                  {food.calories} kcal
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Preparation Instructions */}
                      {(meal.instructions || meal.notes) && (
                        <div className="space-y-1 p-2.5 rounded-xl bg-white/5 border border-white/5">
                          <p className="text-[10px] font-black uppercase tracking-wider text-sky-400 flex items-center gap-1">
                            <BookOpen className="w-3 h-3" /> Instructions & Prep
                          </p>
                          <p className="text-slate-300 text-[11px] leading-relaxed">
                            {meal.instructions || meal.notes}
                          </p>
                        </div>
                      )}

                      {/* Interactive Log Action Button */}
                      <button
                        type="button"
                        onClick={() => onToggleMeal(meal.plan_id, meal.meal_name)}
                        disabled={isMutating}
                        className={`w-full py-2.5 rounded-xl font-bold text-xs sm:text-sm tracking-wide transition-all flex items-center justify-center gap-2 active:scale-95 ${
                          isLogged
                            ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
                            : "bg-sky-500 hover:bg-sky-400 text-white shadow-[0_0_15px_rgba(14,165,233,0.35)]"
                        }`}
                      >
                        <Check className="w-4 h-4 stroke-[3]" />
                        <span>{isLogged ? "Mark as Incomplete" : "Mark as Logged"}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Manual Logged Foods if any */}
          {manualLogs.length > 0 && (
            <div className="pt-2 space-y-2">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Additional Logged Items ({manualLogs.length})
              </p>
              {manualLogs.map((log, lIdx) => (
                <div
                  key={lIdx}
                  className="p-3 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-between gap-3 text-xs"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate capitalize">
                      {log.food_name}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {log.portion || "Manual entry"} · {log.calories} kcal
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleManual(log.id, !log.completed)}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition ${
                      log.completed
                        ? "bg-emerald-500 text-white"
                        : "border border-white/30 text-transparent"
                    }`}
                  >
                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
